import AnniversaryIcon from '@assets/icons/AnniversaryIcon'
import CardIcon from '@assets/icons/CardIcon'
import { colors } from '@globals/themes/defaultTheme'
import { CelebrationType, CelebrationTypeEnum, TenantMemberDisplayData } from '@kudos/http-client'
import CakeOutlined from '@mui/icons-material/CakeOutlined'
import MilitaryTechOutlinedIcon from '@mui/icons-material/MilitaryTechOutlined'
import WavingHandOutlined from '@mui/icons-material/WavingHandOutlined'
import { fullNameFromParts } from '@shared/utils/nameFormatting'
import { T } from '@transifex/react'
import React from 'react'

export const celebrationIcon: Record<CelebrationType, React.ReactElement> = {
  [CelebrationTypeEnum.ANNIVERSARY]: <MilitaryTechOutlinedIcon />,
  [CelebrationTypeEnum.ONBOARDING]: <AnniversaryIcon fill={colors.aquaPrimary} />,
  [CelebrationTypeEnum.BIRTHDAY]: <CakeOutlined />,
  [CelebrationTypeEnum.WELCOME]: <WavingHandOutlined />,
  [CelebrationTypeEnum.OTHER]: <CardIcon />,
}

const celebrationIconColor: Record<CelebrationType, string> = {
  [CelebrationTypeEnum.ANNIVERSARY]: colors.irisPrimary,
  [CelebrationTypeEnum.ONBOARDING]: colors.aquaPrimary,
  [CelebrationTypeEnum.BIRTHDAY]: colors.goldPrimary,
  [CelebrationTypeEnum.WELCOME]: colors.blueMedium,
  [CelebrationTypeEnum.OTHER]: colors.orchidPrimary,
}

const celebrationIconBgColor: Record<CelebrationType, string> = {
  [CelebrationTypeEnum.ANNIVERSARY]: colors.irisExtraLight,
  [CelebrationTypeEnum.ONBOARDING]: colors.aquaExtraLight,
  [CelebrationTypeEnum.BIRTHDAY]: colors.goldExtraLight,
  [CelebrationTypeEnum.WELCOME]: colors.blueExtraLight,
  [CelebrationTypeEnum.OTHER]: colors.orchidExtraLight,
}

const onboardingMessage = (interval: number) => {
  if (!interval) return <T _str="Onboarding" />

  if (interval === 1) {
    return <T _str="First day" interval={interval} />
  }
  return <T _str="First {interval} days" interval={interval} />
}

const anniversaryMessage = (interval: number) => {
  if (!interval) return <T _str="Anniversary" />

  if (interval === 1) {
    return <T _str="First year" interval={interval} />
  }
  return <T _str="{interval} years" interval={interval} />
}

const celebrationDisplay = (interval: number) => ({
  [CelebrationTypeEnum.ANNIVERSARY]: anniversaryMessage(interval),
  [CelebrationTypeEnum.ONBOARDING]: onboardingMessage(interval),
  [CelebrationTypeEnum.BIRTHDAY]: <T _str="Birthday" />,
  [CelebrationTypeEnum.WELCOME]: <T _str="Welcome" />,
  [CelebrationTypeEnum.OTHER]: <T _str="Custom card" />,
})

const celebrationGreeting = (
  interval: number,
  memberDisplayData?: TenantMemberDisplayData | null,
) => {
  const fullName = fullNameFromParts(memberDisplayData?.firstName, memberDisplayData?.lastName)
  return {
    [CelebrationTypeEnum.ANNIVERSARY]: (
      <T
        _str="Happy {interval} year anniversary, {name}!"
        interval={interval}
        name={<b>{fullName}</b>}
      />
    ),
    [CelebrationTypeEnum.ONBOARDING]: (
      <T _str="Happy first {interval} days, {name}!" interval={interval} name={<b>{fullName}</b>} />
    ),
    [CelebrationTypeEnum.BIRTHDAY]: <T _str="Happy birthday, {name}!" name={<b>{fullName}</b>} />,
    [CelebrationTypeEnum.WELCOME]: <T _str="Welcome aboard, {name}!" name={<b>{fullName}</b>} />,
    [CelebrationTypeEnum.OTHER]: undefined,
  }
}

const useCelebrationsDisplayStyle = (
  celebrationType: CelebrationType,
  interval: number = 0,
  memberDisplayData?: TenantMemberDisplayData | null,
) => {
  return {
    celebrationBorderColor: celebrationIconColor[celebrationType],
    celebrationIcon: celebrationIcon[celebrationType],
    celebrationIconColor: celebrationIconColor[celebrationType],
    celebrationIconBgColor: celebrationIconBgColor[celebrationType],
    celebrationDisplay: celebrationDisplay(interval)[celebrationType],
    celebrationGreeting: celebrationGreeting(interval, memberDisplayData)[celebrationType],
  }
}

export default useCelebrationsDisplayStyle
